import type {
  ModuleDefinition,
  SynergyDefinition,
} from "./phase-three-types";

export interface SkillRangeEntry {
  label: string;
  value: number;
}

const MODULE_BONUS_RANGE_FIELDS = [
  ["rangeAdd", "属性强化.射程加成"],
  ["scoutingRadiusAdd", "属性强化.侦查范围加成"],
  ["splashRadius", "属性强化.溅射半径"],
  ["revealRadius", "属性强化.反隐范围"],
] as const;

export function isAllowedSkillRange(value: number): boolean {
  if (!Number.isInteger(value) || value < 0) {
    return false;
  }
  return value < 200 ? value % 5 === 0 : value % 10 === 0;
}

export function normalizeSkillRange(value: number): number {
  if (!Number.isFinite(value) || value < 0) {
    throw new Error("技能范围必须是大于等于0的有限数字。");
  }
  if (isAllowedSkillRange(value)) {
    return value;
  }

  const candidates = new Set([
    Math.floor(value / 5) * 5,
    Math.ceil(value / 5) * 5,
    Math.floor(value / 10) * 10,
    Math.ceil(value / 10) * 10,
    195,
    200,
  ]);
  return [...candidates]
    .filter(isAllowedSkillRange)
    .reduce((best, candidate) => {
      const distance = Math.abs(candidate - value);
      const bestDistance = Math.abs(best - value);
      if (distance < bestDistance) {
        return candidate;
      }
      if (distance === bestDistance && candidate > best) {
        return candidate;
      }
      return best;
    });
}

export function getModuleSkillRangeEntries(
  module: ModuleDefinition,
): SkillRangeEntry[] {
  const entries: SkillRangeEntry[] = [];
  const add = (label: string, value: number | undefined): void => {
    if (value !== undefined) {
      entries.push({ label, value });
    }
  };

  add("攻击效果.射程", module.combat?.range);
  add("攻击效果.范围伤害半径", module.combat?.radius);
  add("伙伴效果.环绕半径", module.companion?.orbitRadius);
  add("伙伴效果.攻击范围", module.companion?.range);
  const movement = module.companion?.movement;
  if (movement?.kind === "player_centered_wander") {
    add("伙伴效果.游荡半径", movement.wanderRadius);
    add("伙伴效果.追赶触发距离", movement.returnTriggerRadius);
    add("伙伴效果.追赶解除距离", movement.returnReleaseRadius);
  } else if (movement?.kind === "player_centered_random") {
    add("伙伴效果.随机移动范围", movement.radius);
  }
  add("治疗效果.治疗范围", module.healing?.range);
  add("考古效果.反隐范围", module.archaeology?.revealRadius);
  for (const [field, label] of MODULE_BONUS_RANGE_FIELDS) {
    add(label, module.bonuses?.[field]);
  }
  for (const [index, mechanic] of (module.mechanics ?? []).entries()) {
    if (mechanic.kind === "guard_periodic_area_damage") {
      add(`护卫机制[${index}].范围伤害半径`, mechanic.radius);
    } else if (mechanic.kind === "earth_spirit_summon") {
      add(`土灵机制[${index}].生成半径`, mechanic.spawnRadius);
      add(`土灵机制[${index}].警戒范围`, mechanic.alertRange);
    } else if (mechanic.kind === "follower_periodic_damage") {
      add(`随从机制[${index}].攻击范围`, mechanic.range);
    } else if (mechanic.kind === "follower_periodic_area_damage") {
      add(`随从机制[${index}].范围伤害半径`, mechanic.radius);
    } else if (mechanic.kind === "periodic_health_extremes_damage") {
      add(`生命极值机制[${index}].作用范围`, mechanic.range);
    } else if (mechanic.kind === "basic_attack_chain") {
      add(`普攻弹射机制[${index}].弹射范围`, mechanic.bounceRange);
    } else if (mechanic.kind === "movement_damage_field") {
      add(`移动伤害场[${index}].作用半径`, mechanic.radius);
    } else if (mechanic.kind === "guard_charge") {
      add(`护卫冲锋[${index}].索敌范围`, mechanic.acquireRange);
      add(`护卫冲锋[${index}].路径宽度`, mechanic.pathWidth);
    } else if (mechanic.kind === "periodic_self_area_damage") {
      add(`自身范围机制[${index}].作用半径`, mechanic.radius);
    } else if (mechanic.kind === "periodic_normal_execute_reward") {
      add(`处决机制[${index}].作用范围`, mechanic.range);
    } else if (mechanic.kind === "periodic_forced_aggro") {
      add(`强制仇恨机制[${index}].作用范围`, mechanic.range);
    }
  }
  return entries;
}

export function getSynergySkillRangeEntries(
  synergy: SynergyDefinition,
): SkillRangeEntry[] {
  const entries: SkillRangeEntry[] = [];
  for (const [field, label] of MODULE_BONUS_RANGE_FIELDS) {
    const value = synergy.bonuses?.[field];
    if (value !== undefined) {
      entries.push({ label: `羁绊${label}`, value });
    }
  }
  for (const [index, modifier] of (
    synergy.abilityModifiers ?? []
  ).entries()) {
    if (modifier.radiusAdd !== undefined) {
      entries.push({
          label: `技能机制调整[${index}].范围伤害半径加成`,
        value: modifier.radiusAdd,
      });
    }
  }
  for (const [index, mechanic] of (synergy.mechanics ?? []).entries()) {
    if (mechanic.kind === "pasture_hunt") {
      entries.push({
        label: `羁绊机制[${index}].游猎范围`,
        value: mechanic.range,
      });
    } else if (mechanic.kind === "periodic_heavy_damage") {
      entries.push({
        label: `羁绊机制[${index}].重装打击范围`,
        value: mechanic.range,
      });
    } else if (mechanic.kind === "guard_dash") {
      entries.push(
        {
          label: `羁绊机制[${index}].冲刺索敌范围`,
          value: mechanic.acquireRange,
        },
        {
          label: `羁绊机制[${index}].冲刺路径宽度`,
          value: mechanic.pathWidth,
        },
      );
    } else if (mechanic.kind === "guard_oath_upgrade") {
      entries.push({
        label: `羁绊机制[${index}].誓约铁卫攻击范围`,
        value: mechanic.range,
      });
    } else if (mechanic.kind === "divine_guard_upgrade") {
      entries.push(
        {
          label: `羁绊机制[${index}].神之代行者射程`,
          value: mechanic.range,
        },
        {
          label: `羁绊机制[${index}].圣火半径`,
          value: mechanic.holyFireRadius,
        },
        {
          label: `羁绊机制[${index}].降临索敌范围`,
          value: mechanic.descentAcquireRange,
        },
        {
          label: `羁绊机制[${index}].降临半径`,
          value: mechanic.descentRadius,
        },
      );
    } else if (mechanic.kind === "earth_spirit_aggregation") {
      entries.push(
        {
          label: `羁绊机制[${index}].聚合范围`,
          value: mechanic.aggregationRange,
        },
        {
          label: `羁绊机制[${index}].小岩灵攻击范围`,
          value: mechanic.baseRange,
        },
        {
          label: `羁绊机制[${index}].巨石统御范围`,
          value: mechanic.rulerAuraRadius,
        },
      );
    } else if (mechanic.kind === "day_night_cycle") {
      entries.push({
        label: `羁绊机制[${index}].日月作用范围`,
        value: mechanic.range,
      });
    } else if (mechanic.kind === "distant_target_bonus") {
      entries.push(
        { label: `羁绊机制[${index}].射程加成`, value: mechanic.rangeAdd },
        { label: `羁绊机制[${index}].远距阈值`, value: mechanic.distanceThreshold },
      );
    } else if (mechanic.kind === "thunderstorm_counter") {
      entries.push({
        label: `羁绊机制[${index}].雷暴半径`,
        value: mechanic.radius,
      });
    } else if (mechanic.kind === "hot_floor") {
      entries.push({
        label: `羁绊机制[${index}].范围加成`,
        value: mechanic.rangeAdd,
      });
    } else if (mechanic.kind === "nest_riot") {
      entries.push({
        label: `羁绊机制[${index}].巢穴联动范围`,
        value: mechanic.radius,
      });
    } else if (mechanic.kind === "slacking_off") {
      entries.push({
        label: `羁绊机制[${index}].自动修复范围`,
        value: mechanic.stationaryRadius,
      });
    }
  }
  return entries;
}

export function normalizeModuleSkillRanges(
  module: ModuleDefinition,
): ModuleDefinition {
  const bonuses = { ...module.bonuses };
  for (const [field] of MODULE_BONUS_RANGE_FIELDS) {
    const value = bonuses[field];
    if (value !== undefined) {
      bonuses[field] = normalizeSkillRange(value);
    }
  }

  return {
    ...module,
    bonuses,
    combat: module.combat
      ? {
          ...module.combat,
          range: normalizeSkillRange(module.combat.range),
          radius:
            module.combat.radius === undefined
              ? undefined
              : normalizeSkillRange(module.combat.radius),
        }
      : undefined,
    companion: module.companion
      ? {
          ...module.companion,
          orbitRadius: normalizeSkillRange(module.companion.orbitRadius),
          range:
            module.companion.range === undefined
              ? undefined
              : normalizeSkillRange(module.companion.range),
          movement: module.companion.movement
            ? module.companion.movement.kind === "player_centered_wander"
              ? {
                  ...module.companion.movement,
                  wanderRadius: normalizeSkillRange(
                    module.companion.movement.wanderRadius,
                  ),
                  returnTriggerRadius: normalizeSkillRange(
                    module.companion.movement.returnTriggerRadius,
                  ),
                  returnReleaseRadius: normalizeSkillRange(
                    module.companion.movement.returnReleaseRadius,
                  ),
                }
              : {
                  ...module.companion.movement,
                  radius: normalizeSkillRange(
                    module.companion.movement.radius,
                  ),
                }
            : undefined,
        }
      : undefined,
    healing: module.healing
      ? {
          ...module.healing,
          range:
            module.healing.range === undefined
              ? undefined
              : normalizeSkillRange(module.healing.range),
        }
      : undefined,
    archaeology: module.archaeology
      ? {
          ...module.archaeology,
          revealRadius:
            module.archaeology.revealRadius === undefined
              ? undefined
              : normalizeSkillRange(module.archaeology.revealRadius),
        }
      : undefined,
    mechanics: module.mechanics?.map((mechanic) => {
      if (mechanic.kind === "earth_spirit_summon") {
        return {
          ...mechanic,
          spawnRadius: normalizeSkillRange(mechanic.spawnRadius),
          alertRange: normalizeSkillRange(mechanic.alertRange),
        };
      }
      if (mechanic.kind === "follower_periodic_damage") {
        return {
          ...mechanic,
          range: normalizeSkillRange(mechanic.range),
        };
      }
      if (mechanic.kind === "follower_periodic_area_damage") {
        return {
          ...mechanic,
          radius: normalizeSkillRange(mechanic.radius),
        };
      }
      if (mechanic.kind === "periodic_health_extremes_damage") {
        return {
          ...mechanic,
          range: normalizeSkillRange(mechanic.range),
        };
      }
      if (mechanic.kind === "basic_attack_chain") {
        return {
          ...mechanic,
          bounceRange: normalizeSkillRange(mechanic.bounceRange),
        };
      }
      if (mechanic.kind === "movement_damage_field") {
        return {
          ...mechanic,
          radius: normalizeSkillRange(mechanic.radius),
        };
      }
      if (mechanic.kind === "guard_charge") {
        return {
          ...mechanic,
          acquireRange: normalizeSkillRange(mechanic.acquireRange),
          pathWidth: normalizeSkillRange(mechanic.pathWidth),
        };
      }
      if (mechanic.kind === "periodic_self_area_damage") {
        return {
          ...mechanic,
          radius: normalizeSkillRange(mechanic.radius),
        };
      }
      if (
        mechanic.kind === "periodic_normal_execute_reward" ||
        mechanic.kind === "periodic_forced_aggro"
      ) {
        return {
          ...mechanic,
          range: normalizeSkillRange(mechanic.range),
        };
      }
      return mechanic;
    }),
  };
}
